/* Session management.
 *
 * The access token lives in memory (via setAccessToken) and expires in
 * minutes; the refresh token is the only thing persisted, and rotating it is
 * the server's job. A 401 from any API call ends the session rather than
 * letting the UI wander around half-authenticated.
 */
import { setAccessToken } from '@/lib/api'

const REFRESH_KEY = 'qorvex.refresh'
const USER_KEY = 'qorvex.user'

export interface SessionUser {
  userId: string
  email: string
  roles: string[]
  permissions: string[]
}

function jwtSub(token: string): string {
  try {
    const payload = token.split('.')[1] ?? ''
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).sub ?? ''
  } catch {
    return ''
  }
}

interface TokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  roles: string[]
  permissions: string[]
}

let refreshTimer: number | null = null
let currentAccess: string | null = null

function scheduleRefresh(expiresIn: number, onExpired: () => void) {
  if (refreshTimer) window.clearTimeout(refreshTimer)
  // Refresh two minutes before expiry, never sooner than 30s from now.
  const delay = Math.max((expiresIn - 120) * 1000, 30_000)
  refreshTimer = window.setTimeout(() => {
    void refreshSession().catch(onExpired)
  }, delay)
}

function adopt(tokens: TokenResponse, email: string, onExpired: () => void): SessionUser {
  currentAccess = tokens.access_token
  setAccessToken(tokens.access_token)
  localStorage.setItem(REFRESH_KEY, tokens.refresh_token)
  const user: SessionUser = {
    userId: jwtSub(tokens.access_token),
    email, roles: tokens.roles, permissions: tokens.permissions,
  }
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  scheduleRefresh(tokens.expires_in, onExpired)
  return user
}

export class LoginError extends Error {
  constructor(message: string, public mfaRequired = false) { super(message) }
}

/** The password was right and the account has MFA: the sign-in is not done
 *  until a code from the authenticator has been verified against this token. */
export class MfaChallengeError extends Error {
  constructor(public challengeToken: string) {
    super('multi-factor authentication required')
    this.name = 'MfaChallengeError'
  }
}

/** Complete an MFA challenge and receive the session. */
export async function verifyMfa(
  challengeToken: string, code: string, email: string, onExpired: () => void,
): Promise<SessionUser> {
  const res = await fetch('/api/v1/auth/mfa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_token: challengeToken, code: code.trim() }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new LoginError(body.detail ?? body.title ?? 'the code was not accepted', true)
  }
  return adopt(body as TokenResponse, email, onExpired)
}

export async function login(
  email: string, password: string, organizationCode: string,
  onExpired: () => void,
): Promise<SessionUser> {
  const res = await fetch('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email, password,
      organization_code: organizationCode || null,
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new LoginError(body.detail ?? body.title ?? 'sign-in failed')
  }
  if (body.mfa_required && body.challenge_token) {
    throw new MfaChallengeError(body.challenge_token as string)
  }
  return adopt(body as TokenResponse, email, onExpired)
}

export async function refreshSession(): Promise<TokenResponse> {
  const refresh = localStorage.getItem(REFRESH_KEY)
  if (!refresh) throw new Error('no session')
  const res = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  })
  if (!res.ok) {
    clearSession()
    throw new Error('session expired')
  }
  const tokens = (await res.json()) as TokenResponse
  currentAccess = tokens.access_token
  setAccessToken(tokens.access_token)
  localStorage.setItem(REFRESH_KEY, tokens.refresh_token)
  return tokens
}

/** Restore a session from the persisted refresh token. Null = show login. */
export async function restore(onExpired: () => void): Promise<SessionUser | null> {
  const stored = localStorage.getItem(USER_KEY)
  if (!stored || !localStorage.getItem(REFRESH_KEY)) return null
  try {
    const tokens = await refreshSession()
    const user = JSON.parse(stored) as SessionUser
    user.userId = jwtSub(tokens.access_token)
    user.roles = tokens.roles
    user.permissions = tokens.permissions
    scheduleRefresh(tokens.expires_in, onExpired)
    return user
  } catch {
    return null
  }
}

export function clearSession() {
  if (refreshTimer) window.clearTimeout(refreshTimer)
  currentAccess = null
  setAccessToken(null)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(USER_KEY)
}

export async function logout() {
  try {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: currentAccess ? { Authorization: `Bearer ${currentAccess}` } : {},
    })
  } catch { /* revocation is best-effort from the client side */ }
  clearSession()
}
